import React from "react";
import {
    Document, Page, Text, View,
    StyleSheet, pdf,
} from "@react-pdf/renderer";

const BORDER     = "0.5pt solid #000";
const FONT_SIZE  = 7; 
const HEADER_BG  = "#BDD7EE";

const styles = StyleSheet.create({
    page: { padding: 20, fontSize: FONT_SIZE, fontFamily: "Helvetica", backgroundColor: "#fff" },
    headerRow:       { flexDirection: "row", alignItems: "center", marginBottom: 10 },
    headerTextBlock: { flex: 1, alignItems: "center" },
    headerTitle:     { fontSize: 10, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 },
    headerSubtitle:  { fontSize: 8, textAlign: "center", marginBottom: 2 },
    
    infoBox:         { marginTop: 5, alignItems: "center", borderTop: "0.5pt solid #000", paddingTop: 5, width: "100%" },
    levelText:       { fontSize: 9, fontFamily: "Helvetica-Bold" },
    examTitle:       { fontSize: 9, fontFamily: "Helvetica-Bold", textDecoration: "underline", marginTop: 2 },

    table:           { width: "100%", borderLeft: BORDER, borderTop: BORDER, marginTop: 10 },
    row:             { flexDirection: "row", minHeight: 18, alignItems: 'stretch' },
    
    // Header spécifique pour rotation des textes
    headerCell: {
        borderRight: BORDER,
        borderBottom: BORDER,
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
    },
    verticalTextContainer: {
        width: "100%",
        height: 60, // Hauteur pour permettre la lecture verticale
        justifyContent: "center",
        alignItems: "center",
    },
    verticalText: {
        transform: "rotate(-90deg)",
        textAlign: "center",
        width: 60,
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
    },

    cell: { 
        borderRight: BORDER, 
        borderBottom: BORDER, 
        justifyContent: "center", 
        paddingHorizontal: 2, 
        paddingVertical: 4 
    },
    cellText:     { fontSize: FONT_SIZE, textAlign: "center" },
    cellTextLeft: { fontSize: FONT_SIZE, textAlign: "left" },
    cellTextBold: { fontFamily: "Helvetica-Bold" },
    rowEven:      { backgroundColor: "#f2f2f2" },
    
    footer: { marginTop: 20, flexDirection: "row", justifyContent: "flex-end" },
    footerText: { fontSize: 8, fontFamily: "Helvetica-Bold" },
});

const RetestCandidatesPDFDocument = ({ results = [], sessionInfo = null, studyLevelName = "" }) => {
    // 1. Extraction des matières
    const ecTitles = [];
    results.forEach(r => {
        if (r.ec_results) {
            Object.keys(r.ec_results).forEach(title => {
                if (!ecTitles.includes(title)) ecTitles.push(title);
            });
        }
    });
    ecTitles.sort();

    // 2. Configuration des largeurs (Style PV source)
    const baseWidths = {
        INDEX: "3%",
        MATRICULE: "14%",
        NOM_PRENOM: "25%",
        SEXE: "3%",
    };
    
    const remainingWidth = 100 - (3 + 14 + 25 + 3);
    const ecColWidth = ecTitles.length > 0 ? (remainingWidth / ecTitles.length) : 0;

    return (
        <Document title="PV_RATTRAPAGE">
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* En-tête (USTTB / FMOS) [cite: 2, 3] */}
                <View style={styles.headerRow}>
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerTitle}>UNIVERSITE DES SCIENCES, DES TECHNIQUES ET DES TECHNOLOGIES DE BAMAKO : USTTB</Text> [cite: 2]
                        <Text style={styles.headerTitle}>FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE : FMOS</Text> [cite: 3]
                        <Text style={styles.headerSubtitle}>
                            SESSION : {(sessionInfo?.label || "SESSION 1").toUpperCase()} — ANNÉE UNIVERSITAIRE : {sessionInfo?.academic_year || "2025-2026"}
                        </Text>
                        
                        <View style={styles.infoBox}>
                            <Text style={styles.levelText}>LISTE DES RÉSULTATS À RATTRAPER</Text>
                            <Text style={styles.examTitle}>CLASSE / NIVEAU : {(studyLevelName || "1ÈRE ANNÉE").toUpperCase()}</Text>
                        </View>
                    </View>
                </View>

                {/* Tableau Style PV  */}
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={[styles.row, { backgroundColor: HEADER_BG }]}>
                        <View style={[styles.headerCell, { width: baseWidths.INDEX }]}><Text style={styles.cellTextBold}>#</Text></View>
                        <View style={[styles.headerCell, { width: baseWidths.MATRICULE }]}><Text style={styles.cellTextBold}>Matricule</Text></View>
                        <View style={[styles.headerCell, { width: baseWidths.NOM_PRENOM }]}><Text style={styles.cellTextBold}>Nom & Prénoms</Text></View>
                        <View style={[styles.headerCell, { width: baseWidths.SEXE }]}><Text style={styles.cellTextBold}>Sexe</Text></View>
                        
                        {ecTitles.map((title, idx) => (
                            <View key={idx} style={[styles.headerCell, { width: `${ecColWidth}%` }]}>
                                <View style={styles.verticalTextContainer}>
                                    <Text style={styles.verticalText}>{title.toUpperCase()}</Text>
                                </View>
                            </View>
                        ))}
                    </View>

                    {/* Data Rows  */}
                    {results.map((r, i) => (
                        <View key={i} style={[styles.row, i % 2 !== 0 ? styles.rowEven : {}]} wrap={false}>
                            <View style={[styles.cell, { width: baseWidths.INDEX }]}><Text style={styles.cellText}>{i + 1}</Text></View>
                            <View style={[styles.cell, { width: baseWidths.MATRICULE }]}><Text style={styles.cellTextBold}>{r.student_number}</Text></View>
                            <View style={[styles.cell, { width: baseWidths.NOM_PRENOM, alignItems: 'flex-start' }]}>
                                <Text style={styles.cellTextBold}>{`${r.last_name?.toUpperCase()} ${r.first_name}`}</Text>
                            </View>
                            <View style={[styles.cell, { width: baseWidths.SEXE }]}><Text style={styles.cellText}>{r.sex}</Text></View>
                            
                            {ecTitles.map((title, idx) => {
                                const note = r.ec_results ? r.ec_results[title] : null;
                                const isRetake = note !== null && note !== "VAL";
                                return (
                                    <View key={idx} style={[styles.cell, { width: `${ecColWidth}%` }]}>
                                        <Text style={[styles.cellText, { color: isRetake ? "red" : "#777", fontFamily: isRetake ? "Helvetica-Bold" : "Helvetica" }]}>
                                            {note === "VAL" ? "-" : (note ?? "-")}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                    ))}
                </View>

                {/* Pied de page [cite: 5] */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Fait à Bamako, le {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </Text> [cite: 5]
                </View>
            </Page>
        </Document>
    );
};

export async function generateRetestPDF(data) {
    const blob = await pdf(<RetestCandidatesPDFDocument {...data} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PV_RATTRAPAGE_${(data.studyLevelName || "NIVEAU").replace(/\s+/g, "_")}.pdf`;
    a.click();
}